import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { ContentLayoutComponent } from '../shared/layout-components/layout/content-layout/content-layout.component';
import { PublicLayoutComponent } from '../shared/layout-components/layout/public-layout/public-layout.component';

import { OriginGuard } from '../shared/guard/origin.guard';
import { CheckoutConfigGuard } from '../shared/guard/checkout-config.guard';
import { LotteryMenuGuard } from '../shared/guard/lottery-menu.guard';
import { AdminGuard } from 'src/app/shared/guard/admin.guard';

import { AccountComponent } from './account/account/account.component';
import { ViewAccountComponent } from './account/view-account/view-account.component';
import { ActivityLogComponent } from './activity-log/activity-log.component';
import { ApiCredentialsComponent } from './api-credentials/api-credentials.component';
import { ApproveComponent } from './withdraw/approve/approve.component';
import { ApproveadminComponent } from './withdraw/approveadmin/approveadmin.component';
import { BankingCredentialsComponent } from './banking-credentials/banking-credentials.component';
import { BetSummaryComponent } from './bet-summary/bet-summary.component';
import { CashInComponent } from './deposit/cashIn/cashIn/cashIn.component';
import { CheckoutConfig } from './checkout-config/checkout-config.component';
import { CommissionsComponent } from './commissions/commissions.component';
import { CommissionsWalletComponent } from './commissions-wallet/commissions-wallet.component';
import { CryptoDepositComponent } from './crypto-deposit/crypto-deposit.component';
import { CryptomoedaComponent } from './cryptomoeda/cryptomoeda.component';
import { DepositComponent } from './deposit/deposit/deposit/deposit.component';
import { DepositPixComponent } from './deposit/depositPix/depositPix/depositPix.component';
import { ExternalCashInComponent } from './deposit/externalCashIn/externalCashIn.component';
import { ExternalCashOutComponent } from './transferencia/externalCashOut/externalCashOut.component';
import { ExtratoComponent } from './extrato/extrato.component';
import { FinanceIndicatorsComponent } from './finance-indicators/finance-indicators.component';
import { FinancialSummaryComponent } from './financial-summary/financial-summary.component';
import { InfractionsComponent } from './infractions/infractions.component';
import { MonitoringComponent } from './monitoring/monitoring.component';
import { OperationIndicatorsComponent } from './operation-indicators/operation-indicators.component';
import { OperatorListComponent } from './operator-list/operator-list.component';
import { OperatorsComponent } from './operators/operators.component';
import { OverviewComponent } from './overview/overview.component';
import { PlayerListComponent } from './player-list/player-list.component';
import { ProductComponent } from './product/product.component';
import { LicensingComponent } from './licensing/licensing.component';
import { RaffleSummaryComponent } from './raffle-summary/raffle-summary.component';
import { ReservaGgrComponent } from './reserva-ggr/reserva-ggr/reserva-ggr.component';
import { SaqueBoletoComponent } from './transferencia/saque-boleto/saque-boleto/saque-boleto.component';
import { SaqueCopiaColaComponent } from './transferencia/saque-copia-cola/saque-copia-cola/saque-copia-cola.component';
import { SubAccountsComponent } from './sub-accounts/sub-accounts.component';
import { SummaryComponent } from './summary/summary.component';
import { TechnicalEventsComponent } from './technical-events/technical-events.component';
import { TransactionalAccountsComponent } from './transactional-accounts/transactional-accounts.component';
import { TransferBalanceComponent } from './transfer-balance/transfer-balance.component';
import { TransferenciaComponent } from './transferencia/transferencia/transferencia/transferencia.component';
import { UserConfigComponent } from './user-config/user-config.component';
import { UserConfigLotComponent } from './user-config-lot/user-config-lot.component';
import { UsersIndicatorsComponent } from './users-indicators/users-indicators.component';
import { WalletComponent } from './wallet/wallet.component';
import { WebhookComponent } from './webhook/webhook.component';

const routes: Routes = [
  {
    path: 'external',
    component: PublicLayoutComponent,
    children: [
      {
        path: 'cashin',
        title: 'Cash In',
        component: ExternalCashInComponent,
      },
      {
        path: 'cashout',
        title: 'Cash Out',
        component: ExternalCashOutComponent,
      },
    ],
  },
  {
    path: '',
    component: ContentLayoutComponent,
    canActivateChild: [LotteryMenuGuard],
    children: [
      {
        path: '',
        data: { mode: 'dark' },
        loadChildren: () =>
          import('./home/home.module').then((m) => m.HomeModule),
      },
      {
        path: '',
        loadChildren: () =>
          import('./dashboard/dashboard.module').then((m) => m.DashboardModule),
      },

      {
        path: 'sub-accounts',
        title: 'Subcontas',
        component: SubAccountsComponent,
      },
      {
        path: 'extrato',
        title: 'Extrato',
        component: ExtratoComponent,
      },
      {
        path: 'resumo',
        title: 'Resumo',
        component: SummaryComponent,
      },
      {
        path: 'infracoes',
        title: 'Infrações',
        component: InfractionsComponent,
      },
      {
        path: 'comissoes',
        title: 'Comissões',
        component: CommissionsComponent,
      },
      {
        path: 'carteira-comissoes',
        title: 'Carteira',
        component: CommissionsWalletComponent,
      },
      {
        path: 'reserva-financeira',
        title: 'Reserva Financeira',
        component: ReservaGgrComponent,
      },
      {
        path: 'transferencia',
        title: 'Transferência',
        component: TransferenciaComponent,
        canActivate: [OriginGuard],
        data: { allowedOrigins: ['BRL'] },
      },
      {
        path: 'saque-copia-cola',
        title: 'Saque Copia e Cola',
        component: SaqueCopiaColaComponent,
        canActivate: [OriginGuard],
        data: { allowedOrigins: ['BRL'] },
      },
      {
        path: 'saque-boleto',
        title: 'Saque Boleto',
        component: SaqueBoletoComponent,
        canActivate: [OriginGuard],
        data: { allowedOrigins: ['BRL'] },
      },
      {
        path: 'transfer-balance',
        title: 'Transferir Saldo',
        component: TransferBalanceComponent,
      },
      {
        path: 'criptomoeda',
        title: 'Criptoativos',
        component: CryptomoedaComponent,
      },
      {
        path: 'cripto',
        title: 'Conversão Cripto',
        component: CryptoDepositComponent,
      },
      {
        path: 'deposito',
        title: 'Depósito',
        component: DepositComponent,
        canActivate: [OriginGuard],
        data: { allowedOrigins: ['BRL'] },
      },
      {
        path: 'depositoPix',
        title: 'Depósito Pix',
        component: DepositPixComponent,
        canActivate: [OriginGuard],
        data: { allowedOrigins: ['BRL'] },
      },
      {
        path: 'cashIn',
        title: 'Cash In',
        component: CashInComponent,
        canActivate: [OriginGuard],
        data: { excludedOrigins: ['BRL'] },
      },
      {
        path: 'wallet',
        title: 'Carteira',
        component: WalletComponent,
      },
      {
        path: 'approve-withdraw',
        title: 'Aprovar Saques',
        component: ApproveComponent,
      },

      {
        path: 'overview',
        title: 'Visão Geral',
        component: OverviewComponent,
      },
      {
        path: 'operation-indicators',
        title: 'Indicadores',
        component: OperationIndicatorsComponent,
      },
      {
        path: 'users-indicators',
        title: 'Indicadores',
        component: UsersIndicatorsComponent,
      },
      {
        path: 'finance-indicators',
        title: 'Indicadores',
        component: FinanceIndicatorsComponent,
      },
      {
        path: 'transactional-accounts',
        title: 'Contas Transacionais',
        component: TransactionalAccountsComponent,
      },
      {
        path: 'product',
        title: 'Modalidade Lotérica',
        component: ProductComponent,
      },
      {
        path: 'licensing',
        title: 'Licenciamentos',
        component: LicensingComponent,
      },
      {
        path: 'operators',
        title: 'Operadores',
        component: OperatorsComponent,
      },
      {
        path: 'player-list',
        title: 'Lista de Jogadores',
        component: PlayerListComponent,
      },
      {
        path: 'monitoring',
        title: 'Monitoramento',
        component: MonitoringComponent,
      },
      {
        path: 'operator-list',
        title: 'Lista de Operadores',
        component: OperatorListComponent,
      },
      {
        path: 'financial-summary',
        title: 'Resumo Financeiro',
        component: FinancialSummaryComponent,
      },
      {
        path: 'bet-summary',
        title: 'Resumo de Apostas',
        component: BetSummaryComponent,
      },
      {
        path: 'raffle-summary',
        title: 'Resumo de Sorteios',
        component: RaffleSummaryComponent,
      },
      {
        path: 'activity-log',
        title: 'Log de Usuários',
        component: ActivityLogComponent,
      },
      {
        path: 'user-config-lot',
        title: 'Configurações de Usuário',
        component: UserConfigLotComponent,
      },

      {
        path: 'account/view',
        title: 'Visualização da Conta',
        component: ViewAccountComponent,
      },
      {
        path: 'api-credentials',
        title: 'Credenciais API',
        component: ApiCredentialsComponent,
      },
      {
        path: 'user-config',
        title: 'Configurações',
        component: UserConfigComponent,
      },
      {
        path: 'webhook',
        title: 'WebHook',
        component: WebhookComponent,
      },
      {
        path: 'banking-credentials',
        title: 'Credenciais Bancárias',
        component: BankingCredentialsComponent,
      },
      {
        path: 'technical-events',
        title: 'Log de Integração',
        component: TechnicalEventsComponent,
      },
      {
        path: 'checkout-config',
        title: 'Checkout',
        component: CheckoutConfig,
        canActivate: [CheckoutConfigGuard],
      },

      {
        path: 'account/account',
        title: 'Contas',
        component: AccountComponent,
        canActivate: [AdminGuard],
      },
      {
        path: 'approve-withdraw-admin',
        title: 'Aprovar Saques',
        component: ApproveadminComponent,
        canActivate: [AdminGuard],
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class ComponentsRouteModule {}